/**
 * ActivitySlug.ts - ActivitySlug Model
 * 
 * CHANGE HISTORY:
 * - 251021_0517 [8786ca9] danielbeer: feat: apply authr pattern to all 43 remaining packages
 * - 251021_0447 [ce7b5e6] danielbeer: feat: automate TypeScript and Actr integration for all packages
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
import { generateId } from '@opcentral/core';


interface ActivitySlugData {
  id?: string;
  timestamp?: number;
  userId?: any;
  sessionId?: any;
  description?: string;
  type?: any;
  context?: any;
}

export class ActivitySlug {
    id: string;
  timestamp: number;
  userId?: string;
  sessionId: any;
  description: string;
  type: any;
  context: any;
  metadata: Record<string, any>;

  constructor(data: ActivitySlugData = {}) {
    this.id = data.id || generateId();
    this.timestamp = data.timestamp || new Date().toISOString();
    this.userId = data.userId || 'system';
    this.sessionId = data.sessionId;
    this.description = data.description || '';
    this.type = data.type || 'action'; // action, document, commit, event
    this.context = data.context || {}; // Additional context
    this.metadata = data.metadata || {};
  }
  
  /**
   * Generate slug string
   * Format: [ISO_timestamp]_[userId]_[sessionId]_[description]
   */
  toString(): any {
    const timestamp = this.timestamp.replace(/[:.]/g, '-');
    const shortSession = this.sessionId ? this.sessionId.substring(0, 8) : 'no-session';
    const desc = this.description.toLowerCase().replace(/\s+/g, '-').substring(0, 40);
    return `${timestamp}_${this.userId}_${shortSession}_${desc}`;
  }
  
  /**
   * Generate filename-safe slug
   */
  toFilename(): any {
    const timestamp = this.timestamp.split('T')[0]; // YYYY-MM-DD
    const time = this.timestamp.split('T')[1].split('.')[0].replace(/:/g, '-'); // HH-MM-SS
    const shortSession = this.sessionId ? this.sessionId.substring(0, 8) : 'no-session';
    const desc = this.description.toLowerCase().replace(/[^a-z0-9]+/g, '-').substring(0, 40);
    return `${timestamp}_${time}_${this.userId}_${shortSession}_${desc}`;
  }
  
  /**
   * Generate markdown header
   */
  toMarkdownHeader(): any {
    return `---
slug: ${this.toString()}
timestamp: ${this.timestamp}
user: ${this.userId}
session: ${this.sessionId}
type: ${this.type}
description: ${this.description}
---`;
  }
  
  /**
   * Parse slug from string
   */
  static fromString(slugString) {
    const parts = slugString.split('_');
    if (parts.length < 4) {
      throw new Error('Invalid slug format');
    }
    
    return new ActivitySlug({
      timestamp: parts[0].replace(/-/g, ':'),
      userId: parts[1],
      sessionId: parts[2],
      description: parts.slice(3).join('_').replace(/-/g, ' ')
    });
  }
  
  toJSON(): ActivitySlugData {
    return {
      id: this.id,
      timestamp: this.timestamp,
      userId: this.userId,
      sessionId: this.sessionId,
      description: this.description,
      type: this.type,
      context: this.context,
      metadata: this.metadata
    };
  }
  
  static fromJSON(json: ActivitySlugData): ActivitySlug {
    return new ActivitySlug(json);
  }
}

export default ActivitySlug;
